import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react"

// ** Third Party Components
import { z } from "zod"
import axios from "axios"
import { parseCookies } from "nookies"
import { useForm } from "react-hook-form"
import { useMutation } from "@tanstack/react-query"
import { zodResolver } from "@hookform/resolvers/zod"
import { X, Check, ChevronsUpDown } from "lucide-react"

// ** Theme Components
import { Input } from "../common/input"
import { Label } from "../common/label"
import { Button } from "../common/button"
import { Badge } from "../common/badge"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../common/dialog"

// ** Type & Constants
import { DictType } from "@pma/types"
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Routes
import {
  CREATE_CONTRACTOR_COMPANY,
  EDIT_CONTRACTOR_COMPANY,
  ADD_CONTRACTOR_COMPANY_USERS,
  REMOVE_CONTRACTOR_COMPANY_USERS,
  FINANCIAL_SERVER_URL,
} from "@pma/utils/api-routes"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"
import { Avatar, AvatarImage } from "../common/avatar"

// Validation Schema
const contractorCompanySchema = z.object({
  contractorCompanyName: z
    .string()
    .min(1, "Contractor company name is required")
    .transform((val) => val.trim()) // Trim whitespace before validation
    .refine((val) => val.length >= 2, {
      message: "Contractor company name must be at least 2 characters",
    })
    .refine((val) => val.length <= 100, {
      message: "Contractor company name must be less than 100 characters",
    })
    .refine((val) => !/^\s*$/.test(val), {
      message: "Contractor company name cannot be only whitespace",
    }),
  taxes: z
    .union([
      z.string().transform((val) => (val === "" ? undefined : parseFloat(val))),
      z.number(),
      z.undefined(),
    ])
    .optional()
    .refine((val) => val === undefined || val === null || (!isNaN(val) && val >= 0 && val <= 100), {
      message: "Taxes must be a number between 0 and 100",
    }),
})

type ContractorCompanyFormData = z.infer<typeof contractorCompanySchema>

interface InsertUpdateContractorCompanyDialogProps {
  refetchList: () => void
  editData?: any
  insertUpdateContractorCompanyDialog: boolean
  setNewContractorCompanyDetails?: Dispatch<SetStateAction<any>>
  setSelectedContractorCompanyDetails?: Dispatch<SetStateAction<any>>
  setInsertUpdateContractorCompanyDialog: Dispatch<SetStateAction<boolean>>
}

const InsertUpdateContractorCompanyDialog = ({
  refetchList,
  editData = null,
  setNewContractorCompanyDetails,
  setSelectedContractorCompanyDetails,
  insertUpdateContractorCompanyDialog,
  setInsertUpdateContractorCompanyDialog,
}: InsertUpdateContractorCompanyDialogProps) => {
  const { translate, locale } = useTranslator()
  const { authConfig } = useAuth()
  const isHebrew = locale === "he"

  const isEditMode = editData !== null

  const accessToken = parseCookies()[ACCESS_TOKEN]
  const dropdownRef = useRef<HTMLDivElement>(null)
  const organizationId = authConfig.last_organization_id
  const organizationUsers = authConfig.organizationUsers || []

  const [searchQuery, setSearchQuery] = useState("")
  const [openUserSelect, setOpenUserSelect] = useState(false)
  const [initialUsers, setInitialUsers] = useState<any[]>([])
  const [selectedUsers, setSelectedUsers] = useState<any[]>([])

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ContractorCompanyFormData>({
    resolver: zodResolver(contractorCompanySchema),
    defaultValues: {
      contractorCompanyName: "",
      taxes: undefined,
    },
  })

  // Set form values when in edit mode
  useEffect(() => {
    if (isEditMode && editData) {
      setValue("contractorCompanyName", editData.name)
      setValue("taxes", editData.taxes ?? undefined)
      const users = editData._users || []
      setSelectedUsers(users)
      setInitialUsers(users)
    } else {
      reset()
      setSelectedUsers([])
      setInitialUsers([])
    }
  }, [isEditMode, editData, setValue, reset, insertUpdateContractorCompanyDialog])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpenUserSelect(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Filter users based on search query
  const filteredUsers = organizationUsers.filter((user: any) =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  // Calculate added and removed users for update mode
  const { addedUsers, removedUsers } = useMemo(() => {
    const initialIds = new Set(initialUsers.map((u) => u.id))
    const selectedIds = new Set(selectedUsers.map((u) => u.id))

    const added = selectedUsers.filter((u) => !initialIds.has(u.id))
    const removed = initialUsers.filter((u) => !selectedIds.has(u.id))

    return { addedUsers: added, removedUsers: removed }
  }, [selectedUsers, initialUsers])

  // API Functions
  const createContractorCompanyApi = async (data: any) => {
    const response = await axios.post(`${FINANCIAL_SERVER_URL}${CREATE_CONTRACTOR_COMPANY}`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const updateContractorCompanyApi = async (data: any) => {
    const response = await axios.post(`${FINANCIAL_SERVER_URL}${EDIT_CONTRACTOR_COMPANY}`, data, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return response.data
  }

  const addContractorUsersApi = async (data: any) => {
    const response = await axios.post(
      `${FINANCIAL_SERVER_URL}${ADD_CONTRACTOR_COMPANY_USERS}`,
      data,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  const removeContractorUsersApi = async (data: any) => {
    const response = await axios.post(
      `${FINANCIAL_SERVER_URL}${REMOVE_CONTRACTOR_COMPANY_USERS}`,
      data,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  // Mutations
  const createContractorCompanyMutation = useMutation({
    mutationKey: ["create-contractor-company"],
    mutationFn: createContractorCompanyApi,
  })

  const updateContractorCompanyMutation = useMutation({
    mutationKey: ["update-contractor-company"],
    mutationFn: updateContractorCompanyApi,
  })

  const addContractorUsersMutation = useMutation({
    mutationKey: ["add-contractor-users"],
    mutationFn: addContractorUsersApi,
  })

  const removeContractorUsersMutation = useMutation({
    mutationKey: ["remove-contractor-users"],
    mutationFn: removeContractorUsersApi,
  })

  const isSubmitting =
    createContractorCompanyMutation.isPending ||
    updateContractorCompanyMutation.isPending ||
    addContractorUsersMutation.isPending ||
    removeContractorUsersMutation.isPending

  const handleClose = () => {
    reset()
    setSelectedUsers([])
    setInitialUsers([])
    setSelectedContractorCompanyDetails?.(null)
    setInsertUpdateContractorCompanyDialog(false)
  }

  const showSuccessToast = (message: string) => {
    toast({
      title: translate("Success"),
      description: translate(message),
      variant: "success",
    })
  }

  const showErrorToast = (error: DictType) => {
    const errorMessage = error?.response?.data?.message
    toast({
      title: translate("Error"),
      description: errorMessage || translate("An error occurred"),
      variant: "error",
    })
  }

  const onFormSubmit = async (data: ContractorCompanyFormData) => {
    try {
      // Prepare taxes value (convert to number or undefined)
      const taxesValue =
        data.taxes !== undefined && data.taxes !== null && !isNaN(Number(data.taxes))
          ? Number(data.taxes)
          : 0

      if (isEditMode && editData) {
        // Update mode
        await updateContractorCompanyMutation.mutateAsync({
          company_id: editData.id,
          name: data.contractorCompanyName.trim(),
          taxes: taxesValue,
        })

        // Remove users if any were removed
        if (removedUsers.length > 0) {
          await removeContractorUsersMutation.mutateAsync({
            company_id: editData.id,
            users_id: removedUsers.map((u) => u.id),
          })
        }

        // Add users if any were added
        if (addedUsers.length > 0) {
          await addContractorUsersMutation.mutateAsync({
            company_id: editData.id,
            users_id: addedUsers.map((u) => u.id),
          })
        }

        showSuccessToast("Contractor Company updated successfully")
      } else {
        // Create mode
        const createResponse = await createContractorCompanyMutation.mutateAsync({
          name: data.contractorCompanyName.trim(),
          taxes: taxesValue,
          organization_id: organizationId,
        })

        // Add selected users to the newly created company
        if (selectedUsers.length > 0 && createResponse?.id) {
          await addContractorUsersMutation.mutateAsync({
            company_id: createResponse.id,
            users_id: selectedUsers.map((u) => u.id),
          })
        }

        if (setNewContractorCompanyDetails) setNewContractorCompanyDetails(createResponse)

        showSuccessToast("Contractor Company added successfully")
      }

      handleClose()
      refetchList()
      setSelectedContractorCompanyDetails?.(null)
    } catch (err) {
      console.error("Failed to save company:", err)
      showErrorToast(err as DictType)
    }
  }

  const toggleUserSelection = (user: any) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.id === user.id)
      if (isSelected) {
        return prev.filter((u) => u.id !== user.id)
      }
      return [...prev, user]
    })
  }

  const removeUser = (userId: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId))
  }

  // Handle taxes input to only allow valid numeric input
  const handleTaxesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      ".",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ]

    if (allowedKeys.includes(e.key)) {
      // Only allow one decimal point
      if (e.key === "." && e.currentTarget.value.includes(".")) {
        e.preventDefault()
      }
      return
    }

    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (e.ctrlKey || e.metaKey) {
      return
    }

    // Block if not a number
    if (!/^\d$/.test(e.key)) {
      e.preventDefault()
    }
  }

  return (
    <Dialog
      open={insertUpdateContractorCompanyDialog}
      onOpenChange={(open) => !open && handleClose()}
    >
      <DialogContent className={cn("sm:max-w-[500px]", isHebrew ? "rtl" : "ltr")}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader>
            <DialogTitle className={isHebrew ? "text-right" : "text-left"}>
              {translate(isEditMode ? "Edit Contractor Company" : "Add Contractor Company")}
            </DialogTitle>
            <DialogDescription className={isHebrew ? "text-right" : "text-left"}>
              {translate(
                isEditMode
                  ? "Update the contractor company details"
                  : "Add a new contractor company to your organization",
              )}
              .
            </DialogDescription>
          </DialogHeader>

          <div className='grid gap-4 py-4'>
            {/* Company Name Input */}
            <div className='grid gap-2'>
              <Label htmlFor='contractorCompanyName'>{translate("Company Name")}</Label>
              <Input
                id='contractorCompanyName'
                {...register("contractorCompanyName")}
                placeholder={translate("Enter company name")}
                className={errors.contractorCompanyName ? "border-red-500" : ""}
              />
              {errors.contractorCompanyName && (
                <p className='text-sm text-red-500'>
                  {translate(errors.contractorCompanyName.message as string)}
                </p>
              )}
            </div>

            {/* Taxes Input */}
            <div className='grid gap-2'>
              <Label htmlFor='taxes'>
                {translate("Taxes")} (%)
                <span className='text-xs text-muted-foreground'> - {translate("Optional")}</span>
              </Label>
              <Input
                id='taxes'
                type='number'
                step='0.01'
                {...register("taxes")}
                onKeyDown={handleTaxesKeyDown}
                placeholder={translate("Enter tax percentage (0-100)")}
                className={errors.taxes ? "border-red-500" : ""}
              />
              {errors.taxes && (
                <p className='text-sm text-red-500'>{translate(errors.taxes.message as string)}</p>
              )}
            </div>

            {/* Multi-Select Users Dropdown */}
            <div className='grid gap-2'>
              <Label>{translate("Assign Users")}</Label>
              <div className='relative w-full' ref={dropdownRef}>
                {/* Trigger Button */}
                <button
                  type='button'
                  onClick={() => setOpenUserSelect(!openUserSelect)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
                    "hover:bg-accent hover:text-accent-foreground",
                    "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                    "disabled:cursor-not-allowed disabled:opacity-50",
                  )}
                >
                  <span className='truncate'>
                    {selectedUsers.length > 0
                      ? `${selectedUsers.length} ${translate("users selected")}`
                      : translate("Select users")}
                  </span>
                  <ChevronsUpDown
                    className={cn("h-4 w-4 shrink-0 opacity-50", isHebrew ? "mr-2" : "ml-2")}
                  />
                </button>

                {/* Dropdown */}
                {openUserSelect && (
                  <div
                    className={cn(
                      "absolute z-50 mt-1 w-full rounded-md border border-input bg-popover shadow-md",
                      isHebrew ? "right-0" : "left-0",
                    )}
                  >
                    {/* Search Input */}
                    <div className='border-b border-input p-2'>
                      <input
                        type='text'
                        placeholder={translate("Search users")}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className={cn(
                          "w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                          "placeholder:text-muted-foreground",
                          "focus:outline-none focus:ring-2 focus:ring-ring",
                          isHebrew ? "text-right" : "text-left",
                        )}
                      />
                    </div>

                    {/* Options List */}
                    <ul
                      className={cn("max-h-[200px] overflow-y-auto p-1", isHebrew ? "rtl" : "ltr")}
                    >
                      {filteredUsers.length === 0 ? (
                        <li className='px-3 py-2 text-center text-sm text-muted-foreground'>
                          {translate("No users found")}
                        </li>
                      ) : (
                        filteredUsers.map((user: any) => {
                          const isSelected = selectedUsers.some((u) => u.id === user.id)
                          return (
                            <li
                              key={user.id}
                              onClick={() => toggleUserSelection(user)}
                              className={cn(
                                "flex cursor-pointer items-center rounded-md px-3 py-2 text-sm",
                                "hover:bg-accent hover:text-accent-foreground",
                                isHebrew ? "rtl" : "ltr",
                              )}
                            >
                              {/* Checkbox */}
                              <div
                                className={cn(
                                  "flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border",
                                  isSelected
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : "border-input",
                                  isHebrew ? "ml-3" : "mr-3",
                                )}
                              >
                                {isSelected && <Check className='h-3 w-3' />}
                              </div>
                              <div className='flex items-center gap-2'>
                                <Avatar className='h-7 w-7'>
                                  <AvatarImage
                                    src={
                                      user?.user_picture?.url ||
                                      `${process.env.PUBLIC_URL}/user-profile.svg`
                                    }
                                  />
                                </Avatar>
                                <span className='truncate'>{user.name}</span>
                              </div>
                            </li>
                          )
                        })
                      )}
                    </ul>
                  </div>
                )}
              </div>

              {/* Selected Users Badges */}
              {selectedUsers.length > 0 && (
                <div className='mt-2 flex flex-wrap gap-2'>
                  {selectedUsers.map((user) => (
                    <Badge
                      key={user.id}
                      variant='secondary'
                      className='flex items-center gap-1 px-2 py-1'
                    >
                      <Avatar className='h-5 w-5'>
                        <AvatarImage
                          src={
                            user?.user_picture?.url || `${process.env.PUBLIC_URL}/user-profile.svg`
                          }
                        />
                      </Avatar>
                      <span className='max-w-[150px] truncate'>{user.name}</span>
                      <button
                        type='button'
                        onClick={() => removeUser(user.id)}
                        className='ml-1 rounded-full hover:bg-muted-foreground/20'
                      >
                        <X className='h-3 w-3' />
                        <span className='sr-only'>
                          {translate("Remove")} {user.name}
                        </span>
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {/* Show pending changes in edit mode */}
              {isEditMode && (addedUsers.length > 0 || removedUsers.length > 0) && (
                <div className='mt-2 space-y-1 text-xs'>
                  {addedUsers.length > 0 && (
                    <p className='text-green-600'>
                      + {addedUsers.length} {translate("user(s) will be added")}
                    </p>
                  )}
                  {removedUsers.length > 0 && (
                    <p className='text-red-600'>
                      - {removedUsers.length} {translate("user(s) will be removed")}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className='flex-col-reverse gap-2 !space-x-0 sm:flex-row sm:gap-0'>
            <DialogClose asChild className={isHebrew ? "!ml-2 sm:!ml-2" : "sm:mr-2"}>
              <Button type='button' variant='outline' onClick={handleClose} disabled={isSubmitting}>
                {translate("Cancel")}
              </Button>
            </DialogClose>
            <Button type='submit' disabled={isSubmitting} loading={isSubmitting}>
              {translate(isEditMode ? "Update" : "Submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default InsertUpdateContractorCompanyDialog
